
import { useState, useCallback, useEffect } from 'react';
import { Message, Conversation, MessageType } from '@/types/chat';
import { chatService } from '@/services/chatService';

export const useChat = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Record<number | string, Message[]>>({});
  const [selectedConversationId, setSelectedConversationId] = useState<number | string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load conversations initially
  useEffect(() => {
    fetchConversations();
  }, []);

  // Fetch all conversations
  const fetchConversations = useCallback(() => {
    setLoading(true);
    try {
      const allConversations = chatService.getAllConversations();
      setConversations(allConversations);
      setUnreadCount(chatService.getTotalUnreadCount());
      
      // Load messages for all conversations
      const messagesObj: Record<number | string, Message[]> = {};
      allConversations.forEach(conv => {
        messagesObj[conv.id] = chatService.getMessages(conv.id);
      });
      setMessages(messagesObj);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Select a conversation
  const selectConversation = useCallback((id: number | string) => {
    setSelectedConversationId(id);
    
    // Mark as read
    try {
      const { conversations: updatedConversations, messages: updatedMessages } = 
        chatService.markConversationAsRead(id);
      
      setConversations(updatedConversations);
      if (updatedMessages) {
        setMessages(prev => ({ ...prev, [id]: updatedMessages }));
      }
      setUnreadCount(chatService.getTotalUnreadCount());
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  }, []);

  // Get current conversation
  const selectedConversation = selectedConversationId 
    ? conversations.find(c => c.id === selectedConversationId) || null
    : null;
    
  // Get current conversation messages
  const selectedMessages = selectedConversationId 
    ? messages[selectedConversationId] || []
    : [];

  // Send a message
  const sendMessage = useCallback((
    text: string, 
    type: MessageType = 'text',
    file?: File
  ) => {
    if (!selectedConversationId) return;
    
    try {
      const result = chatService.sendMessage(selectedConversationId, text, type, file);
      
      if (result) {
        const { conversations: updatedConversations, messages: updatedMessages } = result;
        
        setConversations(updatedConversations);
        setMessages(prev => {
          const updated = { ...prev };
          updated[selectedConversationId] = updatedMessages;
          return updated;
        });
      }
      
      // Clear input
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }, [selectedConversationId]);

  // Create a new conversation
  const createConversation = useCallback((participant: {
    id: number | string;
    name: string;
    avatar?: string;
    condition?: string;
  }) => {
    try {
      const newConversationId = chatService.createConversation(participant);
      fetchConversations(); // Refresh conversations
      return newConversationId;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  }, [fetchConversations]);

  return {
    conversations,
    messages,
    selectedConversationId,
    selectedConversation,
    selectedMessages,
    newMessage,
    setNewMessage,
    loading,
    unreadCount,
    selectConversation,
    sendMessage,
    createConversation,
    fetchConversations
  };
};
